// Team-Lead-only screen: every unassigned lead across both pipelines, oldest
// first, with an instant per-row agent assign. Reuses the exact field writes the
// lead drawers use (updateListingLead / updateBuyerLead).
import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import {
  listenListingLeads,
  listenBuyerLeads,
  updateListingLead,
  updateBuyerLead,
} from "../../utils/firebasefunctions";
import { excludeTestAgents } from "../../utils/testAgents";
import { leadSourceLabel } from "../../utils/leadSourceLabel";
import { selectUnassignedLeads } from "../../utils/unassignedLeads";

const fmtDate = (ts) => {
  const n = Number(ts || 0);
  if (!n) return "—";
  const d = new Date(n);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

const rowKey = (r) => `${r.collection}:${r.id}`;

export default function UnassignedLeadsView() {
  const [{ user, employees, leadsource }] = useStateValue();
  const isAdmin = user?.role === "Admin";

  const [listingLeads, setListingLeads] = useState([]);
  const [buyerLeads, setBuyerLeads] = useState([]);
  const [listingLoaded, setListingLoaded] = useState(false);
  const [buyerLoaded, setBuyerLoaded] = useState(false);
  const [busyKey, setBusyKey] = useState("");

  // Only Team Leads open the listeners — a non-admin who guesses the URL never
  // triggers a read (the render guard below bounces them).
  useEffect(() => {
    if (!isAdmin) return undefined;
    const unsubL = listenListingLeads((rows) => {
      setListingLeads(rows || []);
      setListingLoaded(true);
    });
    const unsubB = listenBuyerLeads((rows) => {
      setBuyerLeads(rows || []);
      setBuyerLoaded(true);
    });
    return () => {
      if (typeof unsubL === "function") unsubL();
      if (typeof unsubB === "function") unsubB();
    };
  }, [isAdmin]);

  const rows = useMemo(
    () => selectUnassignedLeads({ listingLeads, buyerLeads }),
    [listingLeads, buyerLeads],
  );

  const agentOptions = useMemo(
    () =>
      excludeTestAgents(
        (employees || []).filter(
          (a) =>
            (a.role === "Employee" || a.role === "Admin") &&
            a.type !== 1 &&
            a.type !== 2,
        ),
      ).map((a) => ({
        value: a.id,
        label:
          [a.firstname, a.lastname].filter(Boolean).join(" ") || a.name || a.email || a.id,
      })),
    [employees],
  );

  const assign = async (row, agentId) => {
    if (!agentId || busyKey) return;
    const a = (employees || []).find((e) => e.id === agentId);
    const name = a
      ? [a.firstname, a.lastname].filter(Boolean).join(" ") || a.name || a.email || ""
      : "";
    setBusyKey(rowKey(row));
    try {
      const fn = row.pipeline === "listing" ? updateListingLead : updateBuyerLead;
      // NOTE: these helpers swallow their own errors and RESOLVE with
      // { success: false, error } — they never reject. The result must be
      // checked explicitly or a failed write looks like a success.
      const res = await fn(row.id, { assignedAgent: agentId, assignedAgentName: name });
      if (!res?.success) {
        console.error("Assign failed", res?.error);
        toast.error("Could not assign that lead. Please try again.");
      }
    } finally {
      setBusyKey("");
    }
  };

  // Guards run AFTER the hooks above so hook order stays stable.
  if (!user?.role) return null; // auth still resolving
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  const loading = !(listingLoaded && buyerLoaded);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-[#1E293B] mb-1">Unassigned Leads</h1>
      <p className="text-sm text-gray-500 mb-4">
        {loading
          ? "Loading leads…"
          : `${rows.length} lead${rows.length === 1 ? "" : "s"} with no assigned agent, oldest first.`}
      </p>

      {loading ? (
        <p className="text-gray-600">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-gray-600">All leads are assigned. 🎉</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-500 border-b">
                <th className="py-2 pr-3">Pipeline</th>
                <th className="py-2 pr-3">Client</th>
                <th className="py-2 pr-3">Property</th>
                <th className="py-2 pr-3">Stage</th>
                <th className="py-2 pr-3">Source</th>
                <th className="py-2 pr-3">Created</th>
                <th className="py-2 pr-3">Assign</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={rowKey(r)} className="border-b hover:bg-gray-50">
                  <td className="py-2 pr-3">
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded ${
                        r.pipeline === "listing"
                          ? "bg-slate-100 text-linkColor"
                          : "bg-emerald-100 text-emerald-700"
                      }`}
                    >
                      {r.pipeline === "listing" ? "Listing" : "Buyer"}
                    </span>
                  </td>
                  <td className="py-2 pr-3 font-medium">{r.contactName || "(no name)"}</td>
                  <td className="py-2 pr-3">
                    {r.pipeline === "listing" ? r.propertyAddress || "—" : "—"}
                  </td>
                  <td className="py-2 pr-3">{r.stage || "—"}</td>
                  <td className="py-2 pr-3">
                    {leadSourceLabel(r.leadSource || r.source, leadsource) || "—"}
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(r.createdAt)}</td>
                  <td className="py-2 pr-3">
                    {/* Controlled with value="" so it always resets to the
                        placeholder: a failed write never leaves the row looking
                        assigned, and re-picking the same agent still fires. */}
                    <select
                      value=""
                      disabled={busyKey === rowKey(r)}
                      onChange={(e) => assign(r, e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-sm disabled:opacity-50"
                    >
                      <option value="" disabled>
                        {busyKey === rowKey(r) ? "Assigning…" : "Assign agent…"}
                      </option>
                      {agentOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
